import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import libsodium from 'libsodium-wrappers-sumo';

import {
  authApi,
  isTwoFactorRequired,
  SESSION_LIFETIME_OPTIONS,
  type LoginResponse,
  type SessionLifetime,
} from '@/api/auth';
import { ApiError } from '@/api/client';
import { readSessionLifetime, writeSessionLifetime } from '@/stores/sessionPref';
import {
  base64ToBytes,
  bytesToBase64,
  computeAuthHash,
  decryptSym,
  deriveMasterKey,
  ensureSodium,
  kdfParamsFromServer,
} from '@/crypto/sodium';
import { acceptInviteAndSync } from '@/features/auth/acceptInvite';
import { useAuthStore } from '@/stores/auth';
import { readInviteToken } from '@/lib/inviteToken';

type Stage =
  | { kind: 'credentials' }
  | {
      kind: '2fa';
      /** Derived material kept in memory for the second step. */
      authHashB64: string;
      masterKey: Uint8Array;
    };

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  const [params] = useSearchParams();
  const inviteToken = readInviteToken('invite');
  const presetEmail = params.get('email') ?? '';
  // Doar coduri cunoscute: textul afișat nu vine niciodată din URL (un link
  // construit de altcineva nu poate pune un mesaj fals pe pagina de login).
  const noticeCode = params.get('notice');
  const notice =
    noticeCode === 'invite_accepted' || noticeCode === 'session_expired' ? noticeCode : null;
  const recovered = params.get('recovered') === '1';

  const [email, setEmail] = useState(presetEmail);
  const [password, setPassword] = useState('');
  const [lifetime, setLifetime] = useState<SessionLifetime>(() => readSessionLifetime());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<Stage>({ kind: 'credentials' });
  const [method2fa, setMethod2fa] = useState<'totp' | 'recovery'>('totp');
  const [code, setCode] = useState('');
  const stageRef = useRef(stage);
  stageRef.current = stage;

  useEffect(() => {
    setEmail(presetEmail);
  }, [presetEmail]);

  // Cleanup master key on unmount dacă rămâne prins în stage (ex. user închide tab-ul la pas 2)
  useEffect(() => {
    return () => {
      const s = stageRef.current;
      if (s.kind === '2fa') {
        libsodium.memzero(s.masterKey);
      }
    };
  }, []);

  const completeLogin = async (resp: LoginResponse, masterKey: Uint8Array) => {
    const privateKey = await decryptSym(
      {
        ciphertext: base64ToBytes(resp.private_key_encrypted),
        nonce: base64ToBytes(resp.private_key_nonce),
      },
      masterKey,
    );
    const publicKey = libsodium.crypto_scalarmult_base(privateKey);
    const normalized = email.trim().toLowerCase();

    setSession(
      {
        id: resp.user_id,
        email: normalized,
        tenants: resp.tenants,
      },
      masterKey,
      { publicKey, privateKey },
      resp.session_expires_at,
    );

    if (inviteToken) {
      try {
        await acceptInviteAndSync(inviteToken);
      } catch {
        // ignorăm
      }
    }
    navigate('/vault', { replace: true });
  };

  const handleSubmitCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await ensureSodium();
      const normalized = email.trim().toLowerCase();
      const saltResp = await authApi.getSalt(normalized);
      const salt = base64ToBytes(saltResp.kdf_salt);
      const masterKey = await deriveMasterKey(
        password,
        salt,
        kdfParamsFromServer(saltResp.kdf_params),
      );
      const authHashBytes = await computeAuthHash(masterKey);
      const authHashB64 = bytesToBase64(authHashBytes);
      libsodium.memzero(authHashBytes);

      const resp = await authApi.login({
        email: normalized,
        auth_hash: authHashB64,
        session_lifetime_minutes: lifetime,
      });
      writeSessionLifetime(lifetime);

      if (isTwoFactorRequired(resp)) {
        setStage({ kind: '2fa', authHashB64, masterKey });
        setPassword('');
        return;
      }

      await completeLogin(resp, masterKey);
    } catch (err) {
      handleError(err);
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (stage.kind !== '2fa') return;
    setError(null);
    setBusy(true);

    try {
      const normalized = email.trim().toLowerCase();
      const trimmed = code.trim();
      const payload =
        method2fa === 'totp'
          ? {
              email: normalized,
              auth_hash: stage.authHashB64,
              totp_code: trimmed,
              session_lifetime_minutes: lifetime,
            }
          : {
              email: normalized,
              auth_hash: stage.authHashB64,
              recovery_code: trimmed,
              session_lifetime_minutes: lifetime,
            };
      const resp = await authApi.login(payload);
      if (isTwoFactorRequired(resp)) {
        // Imposibil teoretic — dacă serverul tot cere 2FA, e un cod invalid
        setError(t('auth.invalid_code'));
        return;
      }
      await completeLogin(resp, stage.masterKey);
    } catch (err) {
      handleError(err);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const handleError = (err: unknown) => {
    if (err instanceof ApiError) {
      setError(err.status === 401 ? err.detail || t('auth.invalid_credentials') : err.detail);
    } else if (err instanceof Error) {
      setError(`${t('common.error')}: ${err.message}`);
      console.error('login crypto error', err);
    } else {
      setError(`${t('common.error')}: ${String(err)}`);
    }
  };

  const resetToCredentials = () => {
    if (stage.kind === '2fa') libsodium.memzero(stage.masterKey);
    setStage({ kind: 'credentials' });
    setCode('');
    setError(null);
  };

  return (
    <div className="app-main-bg min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="display-title-lg">{t('app.name')}</h1>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>
            {t('app.tagline')}
          </p>
        </div>

        <div className="card">
          {stage.kind === 'credentials' ? (
            <>
              <h2 className="display-title-sm mb-1">{t('auth.login_title')}</h2>
              <p className="text-[13px] mb-6" style={{ color: 'var(--text-muted)' }}>
                {t('auth.login_description')}
              </p>

              <form onSubmit={handleSubmitCredentials} className="space-y-4">
                <div>
                  <label htmlFor="login-page-1" className="section-label no-dot mb-1.5">{t('auth.email')}</label>
                  <input id="login-page-1"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    placeholder="user@example.com"
                    autoComplete="email"
                    disabled={busy}
                  />
                </div>

                <div>
                  <label htmlFor="login-page-2" className="section-label no-dot mb-1.5">{t('auth.master_password')}</label>
                  <input id="login-page-2"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input font-mono"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    disabled={busy}
                  />
                </div>

                <div>
                  <label htmlFor="login-page-3" className="section-label no-dot mb-1.5">{t('auth.session_duration')}</label>
                  <select id="login-page-3"
                    className="input"
                    value={lifetime}
                    onChange={(e) => setLifetime(Number(e.target.value) as SessionLifetime)}
                    disabled={busy}
                  >
                    {SESSION_LIFETIME_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {t(o.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>

                {notice && !error && (
                  <div
                    className="text-[13px] rounded px-3 py-2 border border-[var(--border)] bg-[var(--bg-surface)]"
                    data-testid="login-notice"
                  >
                    {t(`auth.notice_${notice}`)}
                  </div>
                )}
                {recovered && !error && (
                  <div
                    className="text-[13px] rounded px-3 py-2 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800"
                    data-testid="recovered-notice"
                  >
                    {t('recovery.recovered_notice')}
                  </div>
                )}
                {error && <ErrorBanner text={error} />}

                <button type="submit" className="btn-primary w-full" disabled={busy}>
                  {busy ? t('auth.login_deriving') : t('auth.login_submit')}
                </button>
              </form>

              <div
                className="mt-6 pt-4 text-xs text-center"
                style={{ borderTop: '1px solid var(--border)', color: 'var(--text-dim)' }}
              >
                {t('auth.login_no_account')}{' '}
                <Link to="/register" style={{ color: 'var(--accent)' }} className="hover:underline">
                  {t('auth.login_register_link')}
                </Link>
                <div className="mt-2">
                  <Link to="/recover" style={{ color: 'var(--accent)' }} className="hover:underline">
                    {t('recovery.forgot_link')}
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <>
              <h2 className="display-title-sm mb-1">{t('auth.two_factor_title')}</h2>
              <p className="text-[13px] mb-6" style={{ color: 'var(--text-muted)' }}>
                {method2fa === 'totp'
                  ? t('auth.two_factor_totp_hint')
                  : t('auth.two_factor_recovery_hint')}
              </p>

              <form onSubmit={handleSubmit2FA} className="space-y-4">
                <input
                  className="input font-mono text-lg tracking-widest text-center"
                  inputMode={method2fa === 'totp' ? 'numeric' : 'text'}
                  pattern={method2fa === 'totp' ? '\\d{6}' : undefined}
                  maxLength={method2fa === 'totp' ? 6 : 12}
                  value={code}
                  onChange={(e) =>
                    setCode(
                      method2fa === 'totp'
                        ? e.target.value.replace(/\D/g, '').slice(0, 6)
                        : e.target.value.slice(0, 12),
                    )
                  }
                  placeholder={method2fa === 'totp' ? '000000' : 'XXXX-XXXX'}
                  autoFocus
                  disabled={busy}
                />

                {error && <ErrorBanner text={error} />}

                <button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={busy || (method2fa === 'totp' ? code.length !== 6 : code.length < 8)}
                >
                  {busy ? t('auth.two_factor_verifying') : t('auth.two_factor_confirm')}
                </button>

                <button
                  type="button"
                  className="text-xs w-full text-center hover:underline"
                  style={{ color: 'var(--text-muted)' }}
                  onClick={() => {
                    setMethod2fa(method2fa === 'totp' ? 'recovery' : 'totp');
                    setCode('');
                    setError(null);
                  }}
                >
                  {method2fa === 'totp'
                    ? t('auth.use_recovery_code')
                    : t('auth.use_totp_code')}
                </button>

                <button
                  type="button"
                  className="text-xs w-full text-center hover:underline"
                  style={{ color: 'var(--text-dim)' }}
                  onClick={resetToCredentials}
                >
                  {t('auth.back_to_password')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ErrorBanner({ text }: { text: string }) {
  return (
    <div
      className="text-[13px] rounded px-3 py-2"
      style={{
        color: 'var(--danger)',
        background: 'var(--danger-bg)',
        border: '1px solid var(--danger)',
      }}
    >
      {text}
    </div>
  );
}
